<template>
  <PanelShell
    :title="null"
    :subtitle="panel.subtitle"
    :showHeader="false"
    :interaction-active="isPanelInteractionActive"
  >
    <template #actions>
      <button
        v-if="canPin"
        type="button"
        class="rounded-md p-1.5 text-theme-text-muted transition hover:bg-theme-palette-amber/10 hover:text-theme-palette-amber focus:outline-none focus:ring-2 focus:ring-theme-palette-amber/40 dark:hover:bg-theme-palette-amber/20 dark:hover:text-theme-palette-amber"
        :class="{ 'text-theme-palette-amber': isPinned }"
        :aria-label="pinAriaLabel"
        :title="pinAriaLabel"
        @click.stop="togglePin"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path
            v-if="isPinned"
            d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
          />
          <path
            v-else
            d="M12 8.89L13.17 12.5l3.79.32-2.88 2.49.87 3.71L12 17.05l-2.95 1.97.87-3.71-2.88-2.49 3.79-.32L12 8.89M12 6L9.19 12.63 2 13.24l5.46 4.73L5.82 25l6.18-3.73L18.18 25l-1.64-7.03L22 13.24l-7.19-.61L12 6z"
            transform="translate(0 -4)"
          />
        </svg>
      </button>
      <slot name="actions" />
    </template>

    <component
      :is="panelComponent"
      v-bind="panelProps"
      @loading-state-changed="emit('loading-state-changed', $event)"
      @controls-capabilities="emit('controls-capabilities', $event)"
      @controls-changed="emit('controls-changed', $event)"
      @interaction-active-changed="handleInteractionActiveChanged"
    />
  </PanelShell>
</template>

<script setup>
  /* global defineEmits, defineProps */
  import DatasetVisualizationRenderer from '@/components/visualizations/DatasetVisualizationRenderer.vue';
  import { isPanelPinned, setPanelPinned } from '@/features/custom-dashboards/api/PinnedPanelsRepository';
  import { adaptProps, getComponent, resolveEntryOrNull } from '@/components/visualizations/visualizationRegistry';
  import { computed, defineAsyncComponent, inject, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import PanelShell from './PanelShell.vue';

  // All chart components carry Highcharts as a dependency (~2 MB). Using
  // defineAsyncComponent defers their download until the panel type is first
  // rendered, keeping the initial bundle lean.
  //
  // LngCarrierMap is kept as an explicit constant for the legacy 'lngCarrierMap'
  // panel type that predates the registry. New direct-render types registered in
  // visualization-registry.js are handled generically via getComponent().
  const LngCarrierMap = defineAsyncComponent(() =>
    import('@/components/visualizations/map/LngCarrierTrackerMap.vue')
  );
  const KpiVisualization = defineAsyncComponent(() =>
    import('@/components/visualizations/kpi/KpiVisualization.vue')
  );

  const props = defineProps({
    panel: { type: Object, required: true },
    embedCharts: { type: Boolean, default: true },
    controlOverrides: { type: Object, default: null },
    eventScope: { type: String, default: null },
  });

  const emit = defineEmits(['loading-state-changed', 'controls-capabilities', 'controls-changed']);
  const isPanelInteractionActive = ref(false);

  const injectedDashboardId = inject('dashboardId', null);
  const { t } = useI18n();

  const dashboardIdValue = computed(() => {
    const v = injectedDashboardId;
    if (!v) return null;
    if (typeof v === 'object' && 'value' in v) return v.value || null;
    return v;
  });

  const canPin = computed(() => {
    return Boolean(dashboardIdValue.value && props.panel?.id);
  });

  const isPinned = computed(() =>
    canPin.value ? isPanelPinned(dashboardIdValue.value, props.panel.id) : false
  );

  const pinAriaLabel = computed(() =>
    isPinned.value ? t('home.unpin_panel') : t('home.pin_panel')
  );

  async function togglePin() {
    if (!canPin.value) return;
    try {
      await setPanelPinned(dashboardIdValue.value, props.panel.id, !isPinned.value);
    } catch (error) {
      // Reverted by setPanelPinned on failure; nothing to do here.
    }
  }
  const normalizedPanelType = computed(() => {
    if (props.panel.panel_type) return props.panel.panel_type;
    if (props.panel.panelType) return props.panel.panelType;
    return props.panel.type;
  });

  const isKpiVizType = computed(() => {
    const vt = props.panel.visualizationType;
    return vt === 'kpi' || vt === 'kpi_group';
  });

  const isDatasetVisualizationPanel = computed(() => {
    if (normalizedPanelType.value === 'dataset_visualization') return true;
    const registryEntry = resolveEntryOrNull({ key: props.panel.type });
    // Registry entries with renderingMode 'direct' are rendered by DashboardPanel
    // itself (like lngCarrierMap and orderbook/depth) — not through DatasetVisualizationRenderer.
    if (registryEntry?.renderingMode === 'direct') return false;
    // Legacy hard-coded direct-render type (predates registry)
    if (props.panel.type === 'lngCarrierMap') return false;
    // Any other panel type recognised by the registry goes through DatasetVisualizationRenderer
    return !!registryEntry;
  });

  const panelComponent = computed(() => {
    // Legacy custom_graph panels (from removed Graph Builder feature) render nothing.
    if (normalizedPanelType.value === 'custom_graph') return null;
    if (normalizedPanelType.value === 'custom_kpi') return KpiVisualization;
    if (isDatasetVisualizationPanel.value) return DatasetVisualizationRenderer;
    // Generic handling for registry entries with renderingMode 'direct'
    const registryEntry = resolveEntryOrNull({ key: props.panel.type });
    if (registryEntry?.renderingMode === 'direct') return getComponent(registryEntry);
    // Legacy lngCarrierMap fallback (now also resolved via registry above, kept for safety)
    if (props.panel.type === 'lngCarrierMap') return LngCarrierMap;
    return DatasetVisualizationRenderer;
  });

  const handleInteractionActiveChanged = (payload) => {
    if (!isDatasetVisualizationPanel.value) {
      isPanelInteractionActive.value = false;
      return;
    }
    isPanelInteractionActive.value = Boolean(payload?.isActive);
  };

  watch(
    () => [props.panel.id, props.panel.type],
    () => {
      isPanelInteractionActive.value = false;
    }
  );

  const panelProps = computed(() => {
    const mergeOverrides = (base, local) => {
      if (!base && !local) return undefined;

      const merged = {
        ...(base || {}),
        ...(local || {}),
      };

      // Only include sub-objects that have actual values from at least one source.
      // Spreading empty objects (e.g. dateRange: {}) creates truthy empty objects
      // that downstream code (applyControlOverrides) misinterprets as explicit
      // "set to null" overrides, wiping out user-selected values.
      const mergeKey = (key) => {
        const a = base?.[key];
        const b = local?.[key];
        if (a || b) {
          merged[key] = { ...(a || {}), ...(b || {}) };
        } else {
          delete merged[key];
        }
      };

      mergeKey('filters');
      mergeKey('granularity');
      mergeKey('dateRange');
      mergeKey('visibility');
      mergeKey('axes');

      return merged;
    };

    if (normalizedPanelType.value === 'custom_kpi') {
      return {
        chartId: props.panel.id,
        kpiQuery: props.panel.kpiQuery || props.panel.kpi_query || null,
        labelOverrides: props.panel.labelOverrides,
        controlOverrides: mergeOverrides(props.controlOverrides, props.panel.controlOverrides),
        renderOptions: props.panel.renderOptions || {},
        ui: props.embedCharts
          ? {
              chartMinHeight: '120px',
              debounceMs: 250,
            }
          : undefined,
      };
    }
    if (isDatasetVisualizationPanel.value) {
      return {
        chartId: props.panel.id,
        datasetId: props.panel.datasetId,
        visualizationId: props.panel.visualizationId,
        kpiQuery: props.panel.kpiQuery || props.panel.kpi_query || null,
        visualizationType: props.panel.visualizationType,
        panelType: props.panel.type,
        eventScope: props.eventScope,
        labelOverrides: props.panel.labelOverrides,
        styleOverrides: props.panel.styleOverrides,
        controlOverrides: mergeOverrides(props.controlOverrides, props.panel.controlOverrides),
        ui: props.embedCharts
          ? {
              rootClass: 'dash-chart-root',
              shellClass: 'dash-chart-shell',
              headerClass: 'dash-chart-header',
              chartBodyClass: 'dash-chart-body',
              chartMinHeight: isKpiVizType.value ? '120px' : '100%',
              debounceMs: 250,
            }
          : undefined,
      };
    }
    if (props.panel.type === 'lngCarrierMap') {
      return {
        showOriginDestFilter: false,
        ui: props.embedCharts
          ? {
              rootClass: 'dash-chart-root',
              shellClass: 'dash-chart-shell',
              headerClass: 'dash-chart-header',
              chartBodyClass: 'dash-chart-body',
              chartMinHeight: '100%',
              debounceMs: 250,
            }
          : undefined,
      };
    }
    // Generic handling for registry entries with renderingMode 'direct'
    const directEntry = resolveEntryOrNull({ key: props.panel.type });
    if (directEntry?.renderingMode === 'direct') {
      return adaptProps(directEntry, {
        controlOverrides: mergeOverrides(props.controlOverrides, props.panel.controlOverrides),
        ui: props.embedCharts
          ? {
              rootClass: 'dash-chart-root',
              shellClass: 'dash-chart-shell',
              headerClass: 'dash-chart-header',
              chartBodyClass: 'dash-chart-body',
              chartMinHeight: '100%',
              debounceMs: 250,
            }
          : undefined,
      });
    }
    return {};
  });
</script>

<!-- dash-chart-* embed styles live in PanelShell.vue so they're available wherever PanelShell is used. -->
